import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { compressImage } from "@/lib/imageCompress";
import {
  AlertTriangle, Plus, ShieldAlert, Check, Clock, Share2, Inbox, Paperclip, X,
} from "lucide-react";

const STATUS_META = {
  pending_review: { label: "Pending Review",   color: "bg-amber-50 text-amber-700",   Icon: Clock },
  pending_ack:    { label: "Pending Ack",      color: "bg-violet-50 text-violet-700", Icon: ShieldAlert },
  acknowledged:   { label: "Acknowledged",     color: "bg-emerald-50 text-emerald-700",Icon: Check },
  shared:         { label: "Shared",           color: "bg-sky-50 text-sky-700",       Icon: Share2 },
};

function fmt(iso) { return iso ? new Date(iso).toLocaleString() : ""; }

function StatusPill({ status }) {
  const m = STATUS_META[status] || STATUS_META.pending_review;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${m.color}`}>
      <m.Icon className="w-3.5 h-3.5" /> {m.label}
    </span>
  );
}

function IncidentDetail({ id, onBack, onChanged, user }) {
  const [inc, setInc] = useState(null);
  const [reviewText, setReviewText] = useState("");
  const [ackText, setAckText] = useState("");
  const [confirmText, setConfirmText] = useState("");
  const [allBranches, setAllBranches] = useState(false);
  const [pickIds, setPickIds] = useState([]);
  const [branches, setBranches] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const res = await api.get(`/incidents/${id}`);
      setInc(res.data);
    } catch (e) { toast.error(formatApiError(e)); onBack(); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  useEffect(() => {
    (async () => {
      if (user?.role !== "school_owner" && user?.role !== "sub_owner") return;
      try {
        const r = await api.get("/branches/my");
        setBranches((r.data || []).filter((b) => b.status === "approved"));
      } catch { /* ignore */ }
    })();
  }, [user]);

  const togglePick = (bid) =>
    setPickIds((arr) => arr.includes(bid) ? arr.filter((x) => x !== bid) : [...arr, bid]);

  const submitReview = async () => {
    if (!reviewText.trim()) return toast.error("Add your remarks");
    setBusy(true);
    try { await api.post(`/incidents/${id}/review`, { owner_remarks: reviewText.trim() });
      toast.success("Review submitted — waiting for raiser's acknowledgement");
      setReviewText(""); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitAck = async () => {
    setBusy(true);
    try { await api.post(`/incidents/${id}/ack`, { ack_remarks: ackText.trim() || null });
      toast.success("Acknowledged"); setAckText(""); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitShare = async () => {
    if (!allBranches && pickIds.length === 0) return toast.error("Pick at least one branch or toggle All branches");
    setBusy(true);
    try { await api.post(`/incidents/${id}/share`, { all_branches: allBranches, branch_ids: allBranches ? [] : pickIds });
      toast.success("Shared with the selected branches");
      await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitConfirm = async () => {
    setBusy(true);
    try { await api.post(`/incidents/${id}/confirm`, { remarks: confirmText.trim() || null });
      toast.success("Confirmation recorded"); setConfirmText(""); await load(); onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!inc) return <div className="p-8 text-center text-slate-500">Loading…</div>;

  const isOwnerTier = user?.role === "school_owner" || user?.role === "sub_owner";
  const isRaiser = inc.raiser_user_id === user?.id;
  const isBranchMember = user?.role === "branch_admin" && !isRaiser && inc.status === "shared"
    && (user.branch_ids || []).some((b) => (inc.shared_branch_ids || []).includes(b));

  return (
    <div className="space-y-5" data-testid="incident-detail">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline">&larr; Back to list</button>

      <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <span>{inc.branch_name}</span> • <span>raised by {inc.raiser_name}</span> • <span>{fmt(inc.created_at)}</span>
            </div>
            <h2 className="font-heading text-2xl font-bold mt-1">{inc.title}</h2>
          </div>
          <StatusPill status={inc.status} />
        </div>
        <p className="whitespace-pre-wrap text-sm text-slate-700">{inc.body}</p>
      </Card>

      {inc.owner_remarks && (
        <Card className="p-6 rounded-3xl border-violet-200 bg-violet-50/40">
          <h3 className="font-heading font-bold text-violet-900 mb-1">Owner remarks</h3>
          <p className="whitespace-pre-wrap text-sm text-slate-700">{inc.owner_remarks}</p>
          <div className="text-xs text-slate-500 mt-1">— {fmt(inc.owner_remarks_at)}</div>
        </Card>
      )}

      {inc.raiser_ack_at && (
        <Card className="p-6 rounded-3xl border-emerald-200 bg-emerald-50/40">
          <h3 className="font-heading font-bold text-emerald-900 mb-1">Raiser acknowledgement</h3>
          {inc.raiser_ack_remarks ? <p className="whitespace-pre-wrap text-sm text-slate-700">{inc.raiser_ack_remarks}</p> : <p className="text-sm text-slate-500 italic">No additional remarks.</p>}
          <div className="text-xs text-slate-500 mt-1">— {fmt(inc.raiser_ack_at)}</div>
        </Card>
      )}

      {/* Action panels */}
      {isOwnerTier && inc.status === "pending_review" && (
        <Card className="p-6 rounded-3xl border-amber-200 bg-amber-50/30 space-y-3" data-testid="incident-review-panel">
          <h3 className="font-heading font-bold">Add review remarks</h3>
          <Textarea rows={4} value={reviewText} onChange={(e) => setReviewText(e.target.value)} placeholder="Provide guidance, action items, and feedback for the raiser." data-testid="incident-review-input" />
          <Button onClick={submitReview} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="incident-review-submit">Submit review</Button>
        </Card>
      )}

      {isRaiser && inc.status === "pending_ack" && (
        <Card className="p-6 rounded-3xl border-violet-200 bg-violet-50/30 space-y-3" data-testid="incident-ack-panel">
          <h3 className="font-heading font-bold">Acknowledge owner remarks</h3>
          <Textarea rows={3} value={ackText} onChange={(e) => setAckText(e.target.value)} placeholder="Optional additional notes." data-testid="incident-ack-input" />
          <Button onClick={submitAck} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="incident-ack-submit">I acknowledge</Button>
        </Card>
      )}

      {isOwnerTier && inc.status === "acknowledged" && (
        <Card className="p-6 rounded-3xl border-sky-200 bg-sky-50/30 space-y-3" data-testid="incident-share-panel">
          <h3 className="font-heading font-bold">Share with branches</h3>
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={allBranches} onChange={(e) => setAllBranches(e.target.checked)} data-testid="incident-share-all" />
            All branches under this school
          </label>
          {!allBranches && (
            <div className="grid sm:grid-cols-2 gap-2 max-h-60 overflow-y-auto">
              {branches.map((b) => (
                <label key={b.id} className="flex items-center gap-2 text-sm border border-slate-200 rounded-2xl px-3 py-2 cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={pickIds.includes(b.id)} onChange={() => togglePick(b.id)} />
                  {b.name}
                </label>
              ))}
            </div>
          )}
          <Button onClick={submitShare} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="incident-share-submit"><Share2 className="w-4 h-4 mr-1.5" />Share now</Button>
        </Card>
      )}

      {isBranchMember && !inc.my_confirmation && (
        <Card className="p-6 rounded-3xl border-sky-200 bg-sky-50/30 space-y-3" data-testid="incident-confirm-panel">
          <h3 className="font-heading font-bold">Confirm receipt</h3>
          <Textarea rows={3} value={confirmText} onChange={(e) => setConfirmText(e.target.value)} placeholder="Your remarks / feedback (optional)." data-testid="incident-confirm-input" />
          <Button onClick={submitConfirm} disabled={busy} className="rounded-full bg-[#2563EB]" data-testid="incident-confirm-submit">Confirm</Button>
        </Card>
      )}

      {isBranchMember && inc.my_confirmation && (
        <Card className="p-4 rounded-3xl border-emerald-200 bg-emerald-50/30 text-sm text-emerald-800">
          You confirmed this incident on {fmt(inc.my_confirmation.confirmed_at)}.
          {inc.my_confirmation.remarks && <div className="mt-1 italic">"{inc.my_confirmation.remarks}"</div>}
        </Card>
      )}

      {/* Confirmations matrix for owner-tier */}
      {inc.status === "shared" && isOwnerTier && inc.confirmations_summary && (
        <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-4" data-testid="incident-confirmations-summary">
          <div className="flex items-center justify-between">
            <h3 className="font-heading font-bold">Confirmations</h3>
            <span className="text-sm text-slate-600">
              {inc.confirmations_summary.total_confirmed} / {inc.confirmations_summary.total_required} confirmed
            </span>
          </div>
          {inc.confirmations_summary.branches.map((b) => (
            <div key={b.branch_id} className="border border-slate-100 rounded-2xl p-4" data-testid={`incident-branch-${b.branch_id}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="font-heading font-semibold">{b.branch_name}</span>
                <span className="text-xs text-slate-500">{b.confirmed_count}/{b.required_count}</span>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase font-semibold text-emerald-700 mb-1">Confirmed</div>
                  {b.confirmed.length === 0 && <div className="text-xs text-slate-400">No one yet</div>}
                  {b.confirmed.map((u) => (
                    <div key={u.id} className="text-xs py-0.5">
                      <span className="font-medium">{u.name}</span>
                      {u.remarks && <span className="text-slate-500"> — "{u.remarks}"</span>}
                    </div>
                  ))}
                </div>
                <div>
                  <div className="text-xs uppercase font-semibold text-amber-700 mb-1">Pending</div>
                  {b.pending.length === 0 && <div className="text-xs text-slate-400">Everyone has confirmed 🎉</div>}
                  {b.pending.map((u) => (
                    <div key={u.id} className="text-xs py-0.5 text-slate-600">{u.name}</div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

function IncidentCreate({ onBack, onCreated, user }) {
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [branchId, setBranchId] = useState((user?.branch_ids || [])[0] || "");
  const [branches, setBranches] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/branches/my");
        let mine;
        if (user?.role === "school_owner") {
          mine = (r.data || []).filter((b) => b.status === "approved");
        } else {
          mine = (r.data || []).filter((b) => (user?.branch_ids || []).includes(b.id));
        }
        setBranches(mine);
        if (!branchId && mine[0]) setBranchId(mine[0].id);
      } catch { /* ignore */ }
    })();
  // eslint-disable-next-line
  }, []);

  const addFile = async (e) => {
    const f = e.target.files?.[0]; e.target.value = "";
    if (!f) return;
    if (f.type === "application/pdf") {
      const b64 = await new Promise((resolve, reject) => {
        const r = new FileReader(); r.onerror = reject; r.onload = () => resolve(r.result); r.readAsDataURL(f);
      });
      setAttachments((arr) => [...arr, { mime: "application/pdf", base64: b64, name: f.name }]);
    } else if (f.type.startsWith("image/")) {
      try {
        const dataUrl = await compressImage(f, { maxKB: 400, maxDim: 1600 });
        setAttachments((arr) => [...arr, { mime: "image/jpeg", base64: dataUrl, name: f.name }]);
      } catch (err) { toast.error(err.message || "Could not process image"); }
    } else {
      toast.error("Only images or PDFs are allowed");
    }
  };

  const submit = async () => {
    if (!title.trim() || !body.trim() || !branchId) return toast.error("Title, body and branch are required");
    setBusy(true);
    try {
      await api.post("/incidents", { title: title.trim(), body: body.trim(), branch_id: branchId, attachments });
      toast.success("Incident submitted to the school owner");
      onCreated();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-3xl border-[#2563EB]/10 space-y-4" data-testid="incident-create">
      <button onClick={onBack} className="text-sm text-[#1D4ED8] hover:underline self-start">&larr; Back</button>
      <h2 className="font-heading text-2xl font-bold">Raise an incident report</h2>
      <div>
        <Label>Title</Label>
        <Input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} data-testid="incident-title-input" />
      </div>
      <div>
        <Label>Branch</Label>
        <select value={branchId} onChange={(e) => setBranchId(e.target.value)} className="w-full border border-slate-300 rounded-xl px-3 py-2 text-sm" data-testid="incident-branch-select">
          {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
      </div>
      <div>
        <Label>What happened</Label>
        <Textarea rows={6} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Describe what happened, when, and any context the owner needs to review the incident." maxLength={8000} data-testid="incident-body-input" />
      </div>
      <div>
        <Label>Attachments (optional — images or PDF)</Label>
        <div className="flex items-center gap-2 mt-1">
          <input id="incident-file" type="file" accept="image/*,application/pdf" onChange={addFile} className="hidden" data-testid="incident-file-input" />
          <label htmlFor="incident-file" className="cursor-pointer inline-flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 text-sm hover:bg-slate-200">
            <Paperclip className="w-4 h-4" /> Add file
          </label>
          {attachments.map((a, i) => (
            <span key={i} className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-50 rounded-full text-xs">
              {a.name}
              <button onClick={() => setAttachments((arr) => arr.filter((_, j) => j !== i))} className="text-rose-500"><X className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      </div>
      <Button onClick={submit} disabled={busy} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8] w-full sm:w-auto" data-testid="incident-submit-btn">
        Submit incident report
      </Button>
    </Card>
  );
}

export default function IncidentsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState({ mode: "list" }); // {mode:'list'|'create'|'detail', id?}
  const navigate = useNavigate();

  const canRaise = user?.role === "branch_admin" || user?.role === "school_owner";

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/incidents");
      setItems(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  // If route hash contains an id, open it (used by notification deep links)
  useEffect(() => {
    const match = window.location.pathname.match(/\/incidents\/([^/]+)$/);
    if (match) setView({ mode: "detail", id: match[1] });
    // eslint-disable-next-line
  }, []);

  const open = (id) => { setView({ mode: "detail", id }); navigate(`/incidents/${id}`, { replace: false }); };
  const back = () => { setView({ mode: "list" }); navigate("/incidents", { replace: false }); load(); };

  return (
    <>
      <div className="space-y-5" data-testid="incidents-page">
        {view.mode === "list" && (
          <>
            <div className="flex flex-wrap items-end justify-between gap-3">
              <div>
                <p className="text-xs text-slate-400 uppercase font-heading">Incident Reports</p>
                <h1 className="font-heading text-3xl font-bold">Incidents</h1>
                <p className="text-sm text-brand-inkMuted mt-1">Raise, review, share and confirm branch incidents.</p>
              </div>
              {canRaise && (
                <Button onClick={() => setView({ mode: "create" })} className="rounded-full bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="incident-new-btn">
                  <Plus className="w-4 h-4 mr-1.5" /> New incident
                </Button>
              )}
            </div>
            {loading && <p className="text-center text-slate-500 py-8">Loading…</p>}
            {!loading && items.length === 0 && (
              <Card className="p-10 rounded-3xl text-center">
                <Inbox className="w-10 h-10 mx-auto text-slate-300 mb-3" />
                <p className="font-heading font-bold">Nothing here yet</p>
                <p className="text-sm text-slate-500 mt-1">{canRaise ? "Tap New incident to raise one." : "No incidents to review."}</p>
              </Card>
            )}
            <div className="space-y-3">
              {items.map((it) => (
                <button key={it.id} onClick={() => open(it.id)} className="w-full text-left bg-white border border-[#2563EB]/10 rounded-3xl p-5 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid={`incident-row-${it.id}`}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-xs text-slate-500">{it.branch_name} • {it.raiser_name} • {fmt(it.created_at)}</div>
                      <div className="font-heading font-bold text-foreground mt-0.5 truncate">{it.title}</div>
                    </div>
                    <StatusPill status={it.status} />
                  </div>
                </button>
              ))}
            </div>
          </>
        )}
        {view.mode === "create" && (
          <IncidentCreate onBack={back} onCreated={back} user={user} />
        )}
        {view.mode === "detail" && (
          <IncidentDetail id={view.id} onBack={back} onChanged={load} user={user} />
        )}
      </div>
    </>
  );
}
